// file path: src\Pages\Project\Kanban\store\ProjectKanban.slice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Task, { TaskGroup, GroupByColumn, GroupByPositions, TaskStatus } from "../../../../Types/project-task.type";
import {
    groupTasksAction,
    beforeSetTasks,
    addSectionAction,
    sortTaskAction,
    repositionTaskGroupsAction,
} from "../../../../Utils/taskActions";
import {
    addAndRepositionTask,
    addAndRepositionTasks,
    loadGroupPositions,
    removeTasks,
    saveGroupPositions,
    taskReposition,
    tasksReposition
} from "../../functions/task-group-position.functions";

type GroupedTask = {
    [key: string]: Task[]
};

type AddTask = {
    groupIndex: number;
    task: Task;
};

function groupAndSort(tasks: Task[]) {
    // 1️⃣ Group by 'status'
    const grouped = tasks.reduce((acc, item) => {
        const key = item.status;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc;
    }, {} as GroupedTask);

    // 2️⃣ Sort within each group by 'age' (or position/task_id)
    Object.keys(grouped).forEach((key) => {
        grouped[key].sort((a, b) => {
            if (a.task_id === b.task_id) {
                return (a.position - b.position);
            } else {
                return a.task_id - b.task_id;
            }
        });
    });

    // Flatten
    return Object.values(grouped).reduce((acc, item) => [...acc, ...item], []);
}

interface Position {
    groupIndex: number;
    taskIndex: number;
}

interface DragData {
    from?: Position;
    to?: Position;
}

interface ProjectKanbanState {
    taskGroups: TaskGroup[];
    dragData?: DragData;
    activeTask?: Task;
    addTask?: AddTask;
    /**
     * NEW: tracks the collapsed state of columns by ID (e.g. "SectionName-Status").
     */
    collapsedColumns: Record<string, boolean>;
}

const initialState: ProjectKanbanState = {
    taskGroups: [],
    collapsedColumns: {}, // new field initialized empty
};

const projectKanbanSlice = createSlice({
    name: "projectKanban",
    initialState,
    reducers: {
        /**
         * Initializes task groups using tasks grouped by "Section".
         */
        initializeTaskGroups(state, action: PayloadAction<{ tasks: Task[], projectId: number }>) {
            const { tasks, projectId } = action.payload;
            const groupPositions = loadGroupPositions(projectId);
            const groupedTasks = groupTasksAction(tasks, [GroupByColumn.Section]);
            const taskGroups = groupedTasks.map((group) => ({
                ...group,
                tasks: group.tasks.map((task) => ({
                    ...task,
                    sub_tasks: task.sub_tasks.map((subTask) => ({
                        ...subTask,
                    })),
                })),
            }));
            repositionTaskGroupsAction(taskGroups, groupPositions);
            taskGroups.forEach(item => {
                item.tasks = groupAndSort(item.tasks);
            });
            state.taskGroups = taskGroups;
        },

        /**
         * Moves a task from one task group to another.
         */
        moveTask(state, action: PayloadAction<{
            fromGroupIndex: number;
            toGroupIndex: number;
            fromTaskId: number;
            status: TaskStatus;
            toTaskId?: number;
        }>) {
            const { fromGroupIndex, toGroupIndex, fromTaskId, toTaskId, status } = action.payload;

            const sourceGroup = state.taskGroups[fromGroupIndex];
            const destinationGroup = state.taskGroups[toGroupIndex];
            if (!sourceGroup || !destinationGroup) {
                console.warn("Source or destination group not found.");
                return;
            }
            const fromTaskIndex = sourceGroup.tasks.findIndex(item => item.task_id === fromTaskId);
            if (fromTaskIndex < 0) {
                console.warn("Task not found in source group.");
                return;
            }

            const [movedTask] = sourceGroup.tasks.splice(fromTaskIndex, 1);
            movedTask.status = status;

            const toTaskIndex = destinationGroup.tasks.findIndex((item) => item.task_id === toTaskId);
            if (toTaskIndex > -1) {
                destinationGroup.tasks.splice(toTaskIndex + 1, 0, movedTask);
            } else {
                destinationGroup.tasks.push(movedTask);
            }
        },

        /**
         * Saving Drag Data
         */
        updateDragData: (state, action: PayloadAction<DragData | undefined>) => {
            state.dragData = action.payload;
        },

        /**
         * Saving Active Task Data
         */
        setActiveTask: (state, action: PayloadAction<Task | undefined>) => {
            state.activeTask = action.payload;
        },

        /**
         * Saving Add Task Data
         */
        setAddTask: (state, action: PayloadAction<AddTask | undefined>) => {
            state.addTask = action.payload;
        },

        /**
         * Updates the order of sections (draggable reordering).
         */
        repositionTaskGroups: (state, action: PayloadAction<GroupByPositions>) => {
            const groupPositions = action.payload;
            const taskGroups = state.taskGroups;
            repositionTaskGroupsAction(taskGroups, groupPositions);
            state.taskGroups = taskGroups;
        },

        /**
         * Adds a new section to the Kanban board.
         */
        addSection(state, action: PayloadAction<{ section: string; projectId: number }>) {
            const { projectId, section } = action.payload;
            const newTaskGroups = [...state.taskGroups];
            const newTaskGroup: TaskGroup = {
                attributes: { section },
                tasks: []
            };
            newTaskGroups.push(newTaskGroup);
            state.taskGroups = newTaskGroups;
            saveGroupPositions(projectId, newTaskGroups);
        },

        /**
         * Removes a section and updates the task groups.
         */
        removeSection(state, action: PayloadAction<string>) {
            const sectionName = action.payload;
            state.taskGroups = state.taskGroups.filter(
                (group) => group.attributes.section !== sectionName
            );
        },

        /**
         * Moves a section from one position to another.
         */
        moveSection(state, action: PayloadAction<{ fromIndex: number; toIndex: number; projectId: number }>) {
            const { fromIndex, toIndex, projectId } = action.payload;
            const taskGroups = [...state.taskGroups];
            const [movedSection] = taskGroups.splice(fromIndex, 1);
            taskGroups.splice(toIndex, 0, movedSection);
            saveGroupPositions(projectId, taskGroups);
            state.taskGroups = taskGroups;
        },

        /**
         * Updates a section name while keeping tasks intact.
         */
        updateSectionName(state, action: PayloadAction<{ oldName: string; newName: string }>) {
            const { oldName, newName } = action.payload;
            state.taskGroups = state.taskGroups.map((group) =>
                group.attributes.section === oldName
                    ? { ...group, attributes: { section: newName } }
                    : group
            );
        },

        /**
         * Add a task
         */
        addTaskAction(state, action: PayloadAction<string>) {
            const task = action.payload;
            if (!state.addTask) {
                throw new Error("addTask failed. No Temporary Task Found To add");
            }
            const updatedTaskGroup = [...state.taskGroups];
            updatedTaskGroup[state.addTask.groupIndex].tasks.push({
                ...state.addTask.task,
                task,
            });
            state.taskGroups = updatedTaskGroup;
            state.addTask = undefined;
        },

        /**
         * Update a task
         */
        updateTask(state, action: PayloadAction<{ groupIndex?: number; task_id: number; update: Partial<Task> }>) {
            const { task_id, update } = action.payload;
            let groupIndex = action.payload.groupIndex;
            if (groupIndex === undefined) {
                groupIndex = state.taskGroups.findIndex((group) =>
                    group.tasks.some((t) => t.task_id === task_id)
                );
            }
            if (groupIndex === undefined) {
                throw new Error("updateTask failed. Group Index invalid");
            }
            if (!state.taskGroups[groupIndex]) {
                throw new Error("updateTask failed. Group Not Found");
            }
            const updatedTaskGroup = [...state.taskGroups];
            updatedTaskGroup[groupIndex].tasks = updatedTaskGroup[groupIndex].tasks.map((item) => {
                if (item.task_id === task_id) {
                    return {
                        ...item,
                        ...update,
                    };
                }
                return item;
            });
            state.taskGroups = updatedTaskGroup;
        },

        /**
         * This will reposition a single task
         */
        kanbanTaskReposition(state, action: PayloadAction<Task>) {
            const update = action.payload;
            state.taskGroups = taskReposition(
                state.taskGroups,
                update,
                [GroupByColumn.Section]
            );
        },

        /**
         * This will reposition multiple tasks
         */
        kanbanTasksReposition(state, action: PayloadAction<Task[]>) {
            const updatedTasks = action.payload;
            state.taskGroups = tasksReposition(
                state.taskGroups,
                updatedTasks,
                [GroupByColumn.Section]
            );
        },

        /**
         * This will add and reposition a task
         */
        kanbanAddAndRepositionTask(state, action: PayloadAction<Task>) {
            const update = action.payload;
            state.taskGroups = addAndRepositionTask(
                state.taskGroups,
                update,
                [GroupByColumn.Section]
            );
        },

        /**
         * This will add and reposition multiple tasks
         */
        kanbanAddAndRepositionTasks(state, action: PayloadAction<Task[]>) {
            const updatedTasks = action.payload;
            state.taskGroups = addAndRepositionTasks(
                state.taskGroups,
                updatedTasks,
                [GroupByColumn.Section]
            );
        },

        /**
         * Removes tasks from task groups based on an array of task IDs.
         */
        kanbanRemoveTasks(state, action: PayloadAction<number[]>) {
            const taskIds = action.payload;
            state.taskGroups = removeTasks(state.taskGroups, taskIds);
        },

        /**
         * NEW: Toggles the collapsed state of a column by its ID (e.g. "Exterior Design-Not Started").
         */
        toggleColumnCollapse(state, action: PayloadAction<string>) {
            const columnId = action.payload;
            const currentValue = !!state.collapsedColumns[columnId];
            state.collapsedColumns[columnId] = !currentValue;
        },
    },
});

export const {
    initializeTaskGroups,
    repositionTaskGroups,
    addSection,
    removeSection,
    updateDragData,
    setAddTask,
    setActiveTask,
    moveSection,
    updateSectionName,
    moveTask,
    addTaskAction,
    updateTask,
    kanbanTaskReposition,
    kanbanTasksReposition,
    kanbanAddAndRepositionTask,
    kanbanAddAndRepositionTasks,
    kanbanRemoveTasks,
    // NEW:
    toggleColumnCollapse,
} = projectKanbanSlice.actions;

const projectKanbanReducer = projectKanbanSlice.reducer;
export default projectKanbanReducer;
